import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { usePublicMode } from "../hooks/usePublicMode";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useState } from "react";

export function Header() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { publicMode } = usePublicMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isDark = location.pathname.startsWith("/shop/");

  const navLink = "overline text-current/80 hover:text-current transition-colors";
  const border = isDark ? "border-b border-white/10" : "border-b border-[var(--gu-border)]";
  const bg = isDark
    ? "bg-[var(--gu-dark-bg)]/70 text-[var(--gu-dark-text)]"
    : "bg-[var(--gu-bg)]/70 text-[var(--gu-primary)]";

  return (
    <header data-testid="site-header" className={`sticky top-0 z-50 backdrop-blur-xl ${bg} ${border}`}>
      <div className="border-b border-[var(--gu-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-3 min-h-[40px]"></div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-6">
        <nav className="hidden md:flex items-center gap-8 flex-1">
          <Link to="/shop" data-testid="nav-shop" className={navLink}>Shop</Link>
          <Link to="/about" data-testid="nav-about" className={navLink}>Story</Link>
          <Link to="/sellers" data-testid="nav-sellers" className={navLink}>Sellers</Link>
        </nav>

        <Link to="/" data-testid="nav-home" className="font-display text-2xl lg:text-3xl tracking-tight leading-none whitespace-nowrap">
          Grace Under Umbrella
        </Link>

        <div className="hidden md:flex items-center gap-6 flex-1 justify-end">
          {user ? (
            <div className="flex items-center gap-4">
              <span className="overline hidden lg:inline" data-testid="user-name">{user.name?.split(" ")[0]}</span>
              <button onClick={logout} data-testid="logout-btn" className={navLink}>Sign out</button>
            </div>
          ) : (
            <button onClick={() => navigate(publicMode ? "/owner-preview" : "/auth")} data-testid="signin-btn" className={`${navLink} flex items-center gap-2`}>
              <User size={16} strokeWidth={1.5} /> Sign in
            </button>
          )}
          <button onClick={() => setOpen(true)} data-testid="cart-toggle" className="flex items-center gap-2 relative">
            <ShoppingBag size={18} strokeWidth={1.5} />
            <span className="overline">Cart</span>
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-3 text-[10px] font-semibold bg-[var(--gu-primary)] text-[var(--gu-bg)] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {count}
              </span>
            )}
          </button>
        </div>

        <button className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[var(--gu-border)] bg-[var(--gu-bg)]">
          <div className="px-6 py-6 flex flex-col gap-5">
            <Link to="/shop" onClick={() => setMobileOpen(false)} className={navLink}>Shop</Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className={navLink}>Story</Link>
            <Link to="/sellers" onClick={() => setMobileOpen(false)} className={navLink}>Sellers</Link>
            {user ? (
              <button onClick={() => { logout(); setMobileOpen(false); }} className={navLink}>Sign out</button>
            ) : (
              <Link to={publicMode ? "/owner-preview" : "/auth"} onClick={() => setMobileOpen(false)} className={navLink}>Sign in</Link>
            )}
            <button onClick={() => { setOpen(true); setMobileOpen(false); }} className={navLink}>Cart ({count})</button>
          </div>
        </div>
      )}
    </header>
  );
}
